import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories, siteImages } from '../data/mock';
import { ShoppingCart, Filter, Grid, List, ChevronDown, X, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);
  const { addToCart } = useCart();
  
  const selectedCategory = searchParams.get('category');

  useEffect(() => {
    document.title = 'Keto Diet Supplements & Daily Support | Keto Vybe';
  }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    
    if (selectedCategory) {
      result = result.filter(p => p.categorySlug === selectedCategory);
    }
    
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }
    
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategorySelect = (categorySlug) => {
    if (categorySlug) {
      setSearchParams({ category: categorySlug });
    } else {
      setSearchParams({});
    }
    setShowFilters(false);
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };

  const currentCategory = categories.find(c => c.slug === selectedCategory);

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-20">
        {/* Banner */}
        <section className="relative h-64 lg:h-80 overflow-hidden">
          <img 
            src={siteImages.shopBanner}
            alt="Keto supplements"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Premium Collection</span>
              <h1 className="font-display text-4xl lg:text-5xl text-white mb-3">
                {currentCategory ? currentCategory.name : 'All Products'}
              </h1>
              <p className="text-slate-400 max-w-md mx-auto">
                {currentCategory ? currentCategory.description : 'Browse our complete keto supplement collection'}
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Desktop */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6 sticky top-28">
                <h3 className="font-bold text-white mb-5">Categories</h3>
                <ul className="space-y-1">
                  <li>
                    <button
                      onClick={() => handleCategorySelect(null)}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all text-sm font-medium ${
                        !selectedCategory
                          ? 'bg-vybe-600 text-white'
                          : 'text-slate-300 hover:bg-slate-700/50'
                      }`}
                    >
                      All Products
                    </button>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <button
                        onClick={() => handleCategorySelect(cat.slug)}
                        className={`w-full text-left px-4 py-3 rounded-xl transition-all text-sm font-medium ${
                          selectedCategory === cat.slug
                            ? 'bg-vybe-600 text-white'
                            : 'text-slate-300 hover:bg-slate-700/50'
                        }`}
                      >
                        {cat.name}
                        <span className="float-right text-slate-500">{cat.productCount}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                  <p className="text-slate-400 text-sm">
                    <span className="text-white font-semibold">{filteredProducts.length}</span> products
                  </p>
                  
                  {/* Mobile Filter Button */}
                  <button
                    onClick={() => setShowFilters(true)}
                    className="lg:hidden flex items-center gap-2 px-4 py-2.5 bg-slate-800 text-white rounded-xl text-sm font-medium"
                  >
                    <Filter className="w-4 h-4" />
                    Filters
                  </button>
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Sort */}
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none px-4 py-2.5 pr-10 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-vybe-500"
                    >
                      <option value="featured">Featured</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="name">Name</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                  
                  {/* View Toggle */}
                  <div className="hidden sm:flex bg-slate-800 rounded-xl p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-vybe-600 text-white' : 'text-slate-400 hover:text-white'}`}
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-vybe-600 text-white' : 'text-slate-400 hover:text-white'}`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Products Grid */}
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6'
                : 'space-y-4'
              }>
                {filteredProducts.map((product) => (
                  viewMode === 'grid' ? (
                    <Link
                      key={product.id}
                      to={`/product/${product.slug}`}
                      className="group bg-slate-800/50 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-vybe-500/50 transition-all hover:shadow-card-hover"
                    >
                      <div className="aspect-square bg-gradient-to-br from-slate-800 to-slate-900 relative overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        {product.badge && (
                          <span className="absolute top-3 left-3 px-2.5 py-1 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white text-xs font-bold rounded-full">
                            {product.badge}
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        <p className="text-vybe-400 text-xs font-semibold uppercase tracking-wider mb-1">
                          {categories.find(c => c.id === product.categoryId)?.name}
                        </p>
                        <h3 className="font-semibold text-white text-sm mb-2 line-clamp-2 group-hover:text-vybe-400 transition-colors">
                          {product.name}
                        </h3>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-white">
                            ${product.price.toFixed(2)}
                          </span>
                          <button
                            onClick={(e) => handleAddToCart(e, product)}
                            className="p-2.5 bg-vybe-600/20 text-vybe-400 rounded-lg hover:bg-vybe-600 hover:text-white transition-all"
                          >
                            <ShoppingCart className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <Link
                      key={product.id}
                      to={`/product/${product.slug}`}
                      className="flex gap-6 bg-slate-800/50 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-vybe-500/50 transition-all p-4"
                    >
                      <div className="w-32 h-32 bg-slate-800 rounded-xl overflow-hidden flex-shrink-0">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 flex flex-col justify-center">
                        <p className="text-vybe-400 text-xs font-semibold uppercase tracking-wider mb-1">
                          {categories.find(c => c.id === product.categoryId)?.name}
                        </p>
                        <h3 className="font-semibold text-white mb-2">{product.name}</h3>
                        <p className="text-slate-400 text-sm line-clamp-2 mb-3">{product.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xl font-bold text-white">${product.price.toFixed(2)}</span>
                          <button
                            onClick={(e) => handleAddToCart(e, product)}
                            className="px-4 py-2 bg-vybe-600 text-white rounded-lg font-semibold text-sm hover:bg-vybe-500 transition-all flex items-center gap-2"
                          >
                            <ShoppingCart className="w-4 h-4" />
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    </Link>
                  )
                ))}
              </div>

              {filteredProducts.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Zap className="w-8 h-8 text-slate-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">No products found</h3>
                  <p className="text-slate-400 mb-6">Try selecting a different category</p>
                  <button
                    onClick={() => handleCategorySelect(null)}
                    className="px-6 py-3 bg-vybe-600 text-white rounded-xl font-semibold"
                  >
                    View All Products
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Filters Drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowFilters(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-slate-900 rounded-t-3xl p-6 max-h-[70vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-white text-lg">Categories</h3>
              <button onClick={() => setShowFilters(false)} className="p-2 text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => handleCategorySelect(null)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                    !selectedCategory ? 'bg-vybe-600 text-white' : 'bg-slate-800 text-slate-300'
                  }`}
                >
                  All Products
                </button>
              </li>
              {categories.map((cat) => (
                <li key={cat.id}>
                  <button
                    onClick={() => handleCategorySelect(cat.slug)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                      selectedCategory === cat.slug ? 'bg-vybe-600 text-white' : 'bg-slate-800 text-slate-300'
                    }`}
                  >
                    {cat.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default ShopPage;
